import { t } from 'i18next'
import { Toast } from 'primereact/toast'
import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { API_DOCUMENT_TYPES, API_DOSSIER_PERSON } from '../../../../constants'
import {
  DossierDb,
  MokaiDocumentType,
  UploadMask,
} from '../../../../model/dossier.model'
import { RequestMethod } from '../../../../model/enums/enums'
import { Person } from '../../../../model/person.model'
import { addDocumentType } from '../../../../redux/basic/dossier/documentTypeAction'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'
import { handleRequest } from '../../../../services/api/base/handleRequest'
import { findPersonById } from '../../../../util/arraySearch'
import { AllPeopleDropdown } from '../../../util/form/dropdown/person-dropdown/AllPeopleDropdown'
import { DocumentUpload } from '../../dossier/DocumentUpload'
import { cutEnding, getDocumentType } from '../../dossier/Helpers'

interface DossierProps {
  files: File[] // the files to be uploaded
  toastRef: React.RefObject<Toast> // toas ref for user notification
  defaultNumber?: number // default person
  deleteCallback: (arg0: number) => void
  closeCallback: () => void
}

/** This is a slimmed down version of DocumentUploadForm
 * In particular, it does not allow further uploads
 * and is embedded into the ocr component
 */
export const AddDossierForm = (props: DossierProps): JSX.Element => {
  const documentTypes = useAppSelector((state) => state.documentTypes)

  const apprentices: Person[] = useAppSelector((state) => state.apprentices)
  const employees = useAppSelector((state) => state.employees)

  const persons = employees.concat(apprentices)
  const [currentDossier, setCurrentDossier] = useState<DossierDb[]>([])
  const account = useAppSelector((state) => state.user)

  const dispatcher = useAppDispatch()

  const methods = useForm({
    reValidateMode: 'onChange',
    mode: 'onSubmit',
    shouldFocusError: false,
    defaultValues: {
      id_person: props.defaultNumber ? props.defaultNumber : account.personId,
    },
  })

  useEffect(() => {
    const id = methods.getValues('id_person')
    if (id) {
      fetchFromDB(id)
    }
  }, [])

  useEffect(() => {
    const subscription = methods.watch(async (value, { type }) => {
      // here we can only change the apprentice
      if (type === 'change') {
        // apprentice id and semester are used to load and reference the report.
        // If anything else is changed it is considered to be a unsaved value the editor wants to keep.
        if (value.id_person) {
          fetchFromDB(value?.id_person)
        }
      }
    })
    return () => subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.watch])

  const fetchFromDB = async (personID: number) => {
    const dossier = await handleRequest<DossierDb[]>(
      API_DOSSIER_PERSON(personID),
      RequestMethod.GET
    )
    setCurrentDossier(dossier.data ? dossier.data : [])
  }

  const fetchDocumentTypes = async () => {
    if (documentTypes.length === 0) {
      const docTypes = await handleRequest<MokaiDocumentType[]>(
        API_DOCUMENT_TYPES(),
        RequestMethod.GET
      )
      docTypes.data?.sort((a, b) =>
        a.document_type_name.localeCompare(b.document_type_name)
      )
      // but move Sonstiges to the back
      const docTypeOther = t<string>('dossier.document_types.1')
      const idx = docTypes.data?.findIndex(
        (a) =>
          a.document_type_name?.toLowerCase() === docTypeOther?.toLowerCase()
      )
      if (idx) {
        docTypes.data?.push(docTypes.data?.splice(idx, 1)[0])
      }
      docTypes.data?.forEach((type) => dispatcher(addDocumentType(type)))
    }
  }

  // we only fetch this once
  useEffect(() => {
    fetchDocumentTypes()
  }, [])

  const um = props.files?.map((v) => {
    const m: UploadMask = {
      document_name: cutEnding(v.name),
      comment: '',
      type: getDocumentType(v.name, documentTypes),
    }
    return m
  })

  return (
    <>
      <FormProvider {...methods}>
        <form>
          <div className={'formgrid grid'}>
            <div className={'grid mt-1 ml-1'}>
              <div className={'md:col col-12 mb-1 w-full'}>
                <AllPeopleDropdown fieldName="id_person" />
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
      <DocumentUpload
        files={props.files}
        currentDossier={currentDossier}
        person={findPersonById(methods.getValues('id_person'), persons)}
        defaultValues={um}
        toastRef={props.toastRef}
        showDeleteButton={props.files ? props.files.length > 1 : false}
        removeFile={props.deleteCallback}
        cleanupCallback={(_) => {
          props.closeCallback()
        }}
      />
    </>
  )
}